import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Add, Refresh, CheckCircle, ErrorOutline, Edit, PersonAddAlt1, Star, LocationOn, Delete, PointOfSale, ViewModule, ViewList } from '@mui/icons-material';
import AdminSidebar from '../../components/admin/AdminSidebar';
import CashierEncashmentSummary from '../../components/admin/CashierEncashmentSummary';
import DashboardHeader from '../../components/admin/DashboardHeader';
import { useAuth } from '../../contexts/AuthContext';
import {
    assignPosCashier,
    createPos,
    fetchCashierProfiles,
    fetchCompanyPos,
    fetchPosCashier,
    setDefaultPos,
    updatePosName,
    verifyNetikashPhone
} from '../../services/posService';
import { fetchSalePoints, createSalePoint, updateSalePoint, deleteSalePoint } from '../../services/salePointService';
import {
    addUserRole,
    buildUserRoleFormPayload,
    fetchOrganizationUserRoles,
    resolveSolutionId,
} from '../../services/userRoleService';
import { fetchCashierEncashmentStats } from '../../services/dashboardService';
import { storageKeys } from '../../config/environment';

const safeParse = (value) => {
    try {
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
};

const CreatePosModal = ({
    open,
    onClose,
    onSubmit,
    form,
    onChange,
    cashiers = [],
    loadingCashiers = false,
    submitting,
    errorMessage,
    onCreateCashier,
}) => {
    if (!open) {
        return null;
    }

    const canSubmit = Boolean(form.managerId);

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Nouveau terminal</h3>
                        <p className="text-sm text-slate-500 mt-0.5">Choisissez un caissier.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                    >
                        Fermer
                    </button>
                </div>

                <form onSubmit={onSubmit} className="p-6 space-y-5">
                    {/* 1. Caissier — requis */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Caissier(e) <span className="text-rose-500">*</span>
                        </label>
                        {loadingCashiers ? (
                            <div className="w-full px-4 py-3 border border-slate-300 rounded-xl text-center text-slate-500">
                                Chargement des caissiers...
                            </div>
                        ) : cashiers.length === 0 ? (
                            <div className="space-y-3">
                                <div className="w-full px-4 py-3 border border-slate-300 rounded-xl text-center text-slate-500 bg-slate-50">
                                    Aucun caissier trouvé
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onCreateCashier && onCreateCashier()}
                                    className="w-full px-4 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
                                >
                                    Créer un caissier
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <select
                                    value={form.managerId || ''}
                                    onChange={(e) => onChange('managerId', e.target.value || null)}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    required
                                >
                                    <option value="">Sélectionnez un caissier</option>
                                    {cashiers.map((cashier) => {
                                        const user = cashier.user || {};
                                        const phone = user.phone?.fullNumber || user.phoneNumber || '';
                                        const name = user.profile?.completeName || user.name || phone || 'Caissier';
                                        return (
                                            <option key={cashier.id} value={user.id}>
                                                {name} {phone ? `(${phone})` : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => onCreateCashier && onCreateCashier()}
                                    className="w-full px-3 py-2 text-sm rounded-xl border border-amber-300 text-amber-600 hover:bg-amber-50"
                                >
                                    + Créer un nouveau caissier
                                </button>
                            </div>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-2 rounded-xl">
                            {errorMessage}
                        </div>
                    )}

                    <div className="flex space-x-3 pt-2">
                        <button
                            type="button"
                            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                            disabled={submitting || !canSubmit}
                        >
                            {submitting ? 'Création...' : 'Créer le POS'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CaisseManagement = () => {
    const { selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');

    const [posList, setPosList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [formState, setFormState] = useState({ managerId: null });
    const [cashiers, setCashiers] = useState([]);
    const [loadingCashiers, setLoadingCashiers] = useState(false);

    // Sites (points de vente)
    const [salePoints, setSalePoints] = useState([]);
    const [loadingSalePoints, setLoadingSalePoints] = useState(false);
    const [siteModalOpen, setSiteModalOpen] = useState(false);
    const [siteForm, setSiteForm] = useState({ name: '', address: '', enabled: true });
    const [editingSiteId, setEditingSiteId] = useState(null);
    const [loadingSiteAction, setLoadingSiteAction] = useState(false);
    
    // Modal pour assigner un caissier (créer une caisse)
    const [assignCashierModalOpen, setAssignCashierModalOpen] = useState(false);
    const [selectedPosForCashier, setSelectedPosForCashier] = useState(null);
    const [assignCashierForm, setAssignCashierForm] = useState({ phoneNumber: '', selectedCashierId: '' });
    const [creatingPos, setCreatingPos] = useState(false);
    const [modalError, setModalError] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);
    
    // Modal pour créer un caissier
    const [createCashierModalOpen, setCreateCashierModalOpen] = useState(false);
    const [createCashierForm, setCreateCashierForm] = useState({ phoneNumber: '' });
    const [creatingCashier, setCreatingCashier] = useState(false);

    // Vue Terminaux : 'table' | 'cards'
    const [terminalViewMode, setTerminalViewMode] = useState('table');
    /** Map cashierId → { dailyAmount, monthlyAmount, yearlyAmount } */
    const [encashmentsByCashier, setEncashmentsByCashier] = useState({});
    const [loadingEncashments, setLoadingEncashments] = useState(false);
    /** Map cashierId → { name, phone } pour la synthèse des encaissements */
    const [cashierProfiles, setCashierProfiles] = useState({});

    const resolvedOrganization = useMemo(() => {
        if (selectedOrganization) {
            return selectedOrganization;
        }
        return safeParse(localStorage.getItem(storageKeys.organisation));
    }, [selectedOrganization]);

    const organizationId = resolvedOrganization?.id || null;
    const organizationName = resolvedOrganization?.name || '—';


    const loadPos = useCallback(async () => {
        if (!organizationId) {
            setPosList([]);
            return;
        }
        setLoading(true);
        setFetchError('');
        try {
            const list = await fetchCompanyPos(organizationId);
            // Dans le mobile, manager_id est directement dans les données du terminal
            // Récupérer les infos des caissiers si nécessaire
            const withAssignments = await Promise.all(
                list.map(async (pos) => {
                    try {
                        // Le manager_id est dans pos.managerId
                        if (pos.managerId) {
                            const cashier = await fetchPosCashier(pos.id, pos.managerId);
                            return { ...pos, cashier };
                        }
                        return { ...pos, cashier: null };
                    } catch (error) {
                        return { ...pos, cashier: null };
                    }
                })
            );
            setPosList(withAssignments);
        } catch (error) {
            setFetchError(error.message || 'Impossible de récupérer vos POS.');
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    const loadEncashments = useCallback(async (terminals) => {
        if (!organizationId || !Array.isArray(terminals) || terminals.length === 0) {
            setEncashmentsByCashier({});
            return;
        }
        const cashierIds = [
            ...new Set(
                terminals
                    .map((p) => p.managerId || p.manager_id)
                    .filter(Boolean)
            ),
        ];
        if (cashierIds.length === 0) {
            setEncashmentsByCashier({});
            return;
        }
        setLoadingEncashments(true);
        // Vider avant de recharger : évite d'afficher un montant sous la mauvaise devise
        setEncashmentsByCashier({});
        try {
            const entries = await Promise.all(
                cashierIds.map(async (cashierId) => {
                    try {
                        const stats = await fetchCashierEncashmentStats({
                            orgId: organizationId,
                            cashierId,
                            currency,
                        });
                        return [cashierId, stats];
                    } catch (error) {
                        console.warn('[loadEncashments]', cashierId, error);
                        return [cashierId, { dailyAmount: 0, monthlyAmount: 0, yearlyAmount: 0 }];
                    }
                })
            );
            setEncashmentsByCashier(Object.fromEntries(entries));
        } finally {
            setLoadingEncashments(false);
        }
    }, [organizationId, currency]);

    const loadSalePoints = useCallback(async () => {
        if (!organizationId) {
            setSalePoints([]);
            return;
        }
        setLoadingSalePoints(true);
        try {
            const result = await fetchSalePoints(organizationId, {});
            setSalePoints(result?.contents ?? []);
        } catch (error) {
            console.error('[loadSalePoints]', error);
            setSalePoints([]);
        } finally {
            setLoadingSalePoints(false);
        }
    }, [organizationId]);

    const openNewSiteModal = () => {
        setEditingSiteId(null);
        setSiteForm({ name: '', address: '', enabled: true });
        setModalError('');
        setSiteModalOpen(true);
    };

    const openEditSiteModal = (site) => {
        setEditingSiteId(site.id);
        setSiteForm({
            name: site.name || '',
            address: site.address || '',
            enabled: site.enabled !== false
        });
        setModalError('');
        setSiteModalOpen(true);
    };

    const handleSaveSite = async (e) => {
        e.preventDefault();
        if (!organizationId) return;
        if (!siteForm.name.trim()) {
            setModalError('Le nom du site est requis.');
            return;
        }
        setLoadingSiteAction(true);
        setModalError('');
        try {
            const payload = {
                company_id: organizationId,
                name: siteForm.name.trim(),
                address: (siteForm.address || '').trim(),
                enabled: !!siteForm.enabled
            };
            if (editingSiteId) {
                await updateSalePoint(editingSiteId, payload);
            } else {
                await createSalePoint(payload);
            }
            await loadSalePoints();
            setSiteModalOpen(false);
            setSiteForm({ name: '', address: '', enabled: true });
            setEditingSiteId(null);
        } catch (err) {
            setModalError(err.message || 'Erreur lors de l\'enregistrement du site.');
        } finally {
            setLoadingSiteAction(false);
        }
    };

    const handleDeleteSite = async (site) => {
        if (!window.confirm(`Supprimer le site « ${site.name} » ? Les terminaux associés peuvent être affectés.`)) return;
        setLoadingSiteAction(true);
        setModalError('');
        try {
            await deleteSalePoint(site.id);
            await loadSalePoints();
            await loadPos();
        } catch (err) {
            setModalError(err.message || 'Erreur lors de la suppression.');
        } finally {
            setLoadingSiteAction(false);
        }
    };

    useEffect(() => {
        if (organizationId) {
            loadPos();
            loadSalePoints();
        }
    }, [organizationId, loadPos, loadSalePoints]);

    useEffect(() => {
        if (!organizationId) {
            setEncashmentsByCashier({});
            return;
        }
        loadEncashments(posList);
    }, [organizationId, posList, loadEncashments]);

    useEffect(() => {
        let cancelled = false;
        const ids = posList.map((p) => p.managerId || p.manager_id).filter(Boolean);
        fetchCashierProfiles(ids).then((profiles) => {
            if (!cancelled) setCashierProfiles(profiles);
        });
        return () => { cancelled = true; };
    }, [posList]);

    const loadCashiers = useCallback(async () => {
        if (!organizationId) return;
        try {
            setLoadingCashiers(true);
            const response = await fetchOrganizationUserRoles(organizationId, {
                pge: 1,
                lmt: 100,
                usr: 'all',
                rol: 'cashier',
                sts: 'all',
            });
            setCashiers(response.contents || []);
        } catch (error) {
            console.error('[loadCashiers] Erreur:', error);
            setCashiers([]);
        } finally {
            setLoadingCashiers(false);
        }
    }, [organizationId]);

    useEffect(() => {
        if ((createModalOpen || assignCashierModalOpen) && organizationId) {
            loadCashiers();
        }
    }, [createModalOpen, assignCashierModalOpen, organizationId, loadCashiers]);

    const resetModal = () => {
        setFormState({ managerId: null });
        setModalError('');
    };

    const handleCreateCashier = async (event) => {
        event.preventDefault();
        if (!organizationId) {
            setModalError('Aucune organisation sélectionnée.');
            return;
        }
        if (!createCashierForm.phoneNumber.trim()) {
            setModalError('Veuillez entrer un numéro de téléphone.');
            return;
        }
        
        const normalized = createCashierForm.phoneNumber.replace(/\s+/g, '');
        if (normalized.length < 8) {
            setModalError('Numéro de téléphone invalide.');
            return;
        }
        
        setCreatingCashier(true);
        setModalError('');
        
        try {
            // Vérifier que le numéro est associé à un compte Netikash
            const verification = await verifyNetikashPhone(normalized);
            if (verification?.step && verification.step !== 'VALIDATED') {
                throw new Error("Ce numéro n'est pas associé à un compte Netikash.");
            }
            
            // Récupérer le solution_id depuis localStorage
            const solutionId = await resolveSolutionId();

            const newCashier = await addUserRole(
                organizationId,
                buildUserRoleFormPayload(organizationId, {
                    user_id: normalized,
                    role_id: 'cashier',
                    solution_id: solutionId,
                })
            );
            
            // Recharger la liste des caissiers
            await loadCashiers();
            
            // Fermer le modal et sélectionner le nouveau caissier dans le formulaire
            setCreateCashierModalOpen(false);
            setCreateCashierForm({ phoneNumber: '' });
            
            // Si on est dans le modal de création de POS, sélectionner le nouveau caissier
            if (createModalOpen && newCashier?.user?.id) {
                setFormState(prev => ({ ...prev, managerId: newCashier.user.id }));
            }
            
            // Si on est dans le modal d'assignation, sélectionner le nouveau caissier
            if (assignCashierModalOpen && newCashier?.user?.id) {
                setAssignCashierForm(prev => ({ 
                    ...prev, 
                    selectedCashierId: newCashier.user.id,
                    phoneNumber: ''
                }));
            }
        } catch (error) {
            setModalError(error.message || 'Impossible de créer ce caissier.');
        } finally {
            setCreatingCashier(false);
        }
    };

    const handleCreatePos = async (event) => {
        event.preventDefault();
        if (!organizationId) {
            setModalError('Aucune organisation sélectionnée.');
            return;
        }
        if (!formState.managerId) {
            setModalError('Veuillez sélectionner un caissier.');
            return;
        }
        setCreatingPos(true);
        setModalError('');
        
        try {
            const createdPos = await createPos({
                companyId: organizationId,
                managerId: formState.managerId,
            });
            
            // Vérifier que le POS a bien été créé
            if (!createdPos || !createdPos.id) {
                console.error('[handleCreatePos] Le POS n\'a pas été créé correctement:', createdPos);
                setModalError('Le POS a été créé mais les données retournées sont invalides. Veuillez rafraîchir la page.');
                return;
            }
            
            console.log('[handleCreatePos] POS créé avec succès:', createdPos);

            window.dispatchEvent(
                new CustomEvent('pos:created', {
                    detail: { organizationId, terminalId: createdPos.id }
                })
            );
            
            // Fermer le modal et réinitialiser le formulaire uniquement si la création a réussi
            setCreateModalOpen(false);
            resetModal();
            
            // Recharger la liste des POS après un court délai pour s'assurer que le serveur a traité la création
            setTimeout(async () => {
                console.log('[handleCreatePos] Rechargement de la liste des POS...');
                await loadPos();
            }, 500);
        } catch (error) {
            console.error('[handleCreatePos] Erreur lors de la création du POS:', error);
            const errorMessage = error?.message || error?.response?.data?.message || 'Impossible de créer ce POS. Veuillez réessayer.';
            setModalError(errorMessage);
        } finally {
            setCreatingPos(false);
        }
    };

    const handleRenamePos = async (pos) => {
        const newName = window.prompt('Nouveau nom du POS', pos.name);
        if (!newName) {
            return;
        }
        const trimmed = newName.trim();
        if (!trimmed || trimmed === pos.name) {
            return;
        }
        setActionLoadingId(pos.id);
        try {
            await updatePosName(pos.id, trimmed);
            setPosList((prev) => prev.map((item) => (item.id === pos.id ? { ...item, name: trimmed } : item)));
        } catch (error) {
            alert(error.message || 'Impossible de renommer ce POS.');
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleAssignCashier = (pos) => {
        setSelectedPosForCashier(pos);
        setAssignCashierForm({ 
            phoneNumber: pos.cashier || '', 
            selectedCashierId: '' 
        });
        setAssignCashierModalOpen(true);
    };

    const handleSubmitAssignCashier = async (event) => {
        event.preventDefault();
        if (!selectedPosForCashier) return;
        
        setActionLoadingId(selectedPosForCashier.id);
        setModalError('');
        
        try {
            let managerId = null;
            
            // Si on a sélectionné un caissier de la liste, utiliser directement son ID
            if (assignCashierForm.selectedCashierId) {
                managerId = assignCashierForm.selectedCashierId;
            } else if (assignCashierForm.phoneNumber.trim()) {
                // Si on a entré un numéro de téléphone, vérifier et récupérer l'ID de l'utilisateur
                const normalized = assignCashierForm.phoneNumber.replace(/\s+/g, '');
                if (normalized.length < 8) {
                    throw new Error('Numéro de téléphone invalide.');
                }
                
                // Vérifier que le numéro est associé à un compte Netikash
                const verification = await verifyNetikashPhone(normalized);
                if (verification?.step && verification.step !== 'VALIDATED') {
                    throw new Error("Ce numéro n'est pas associé à un compte Netikash.");
                }
                
                // Récupérer l'ID de l'utilisateur à partir du numéro de téléphone
                // Chercher d'abord dans les caissiers existants
                const existingCashier = cashiers.find(c => {
                    const phone = c.user?.phone?.fullNumber || c.user?.phoneNumber || '';
                    return phone.replace(/\s+/g, '') === normalized;
                });
                
                if (existingCashier?.user?.id) {
                    managerId = existingCashier.user.id;
                } else {
                    // Si pas trouvé, chercher dans tous les utilisateurs
                    // Note: L'API peut accepter le numéro directement, mais essayons d'abord de trouver l'ID
                    // Pour l'instant, utilisons le numéro comme fallback (l'API peut l'accepter)
                    managerId = normalized;
                }
            } else {
                throw new Error('Veuillez sélectionner un caissier ou entrer un numéro de téléphone.');
            }
            
            // Dans le mobile, on utilise PUT /pos/payment-terminals/{id} avec company_id et manager_id
            if (!organizationId) {
                throw new Error('Aucune organisation sélectionnée.');
            }
            await assignPosCashier(selectedPosForCashier.id, managerId, organizationId);
            
            // Recharger la liste des POS pour avoir les données à jour
            await loadPos();
            
            setAssignCashierModalOpen(false);
            setSelectedPosForCashier(null);
            setAssignCashierForm({ phoneNumber: '', selectedCashierId: '' });
        } catch (error) {
            setModalError(error.message || 'Impossible d\'assigner ce caissier.');
        } finally {
            setActionLoadingId(null);
        }
    };

    /** Terminal par défaut de l'organisation (côté serveur) : utilisé par les caissiers sans terminal. */
    const handleSetActivePos = async (pos) => {
        if (pos.isDefault) return;
        setActionLoadingId(pos.id);
        try {
            await setDefaultPos(pos, organizationId);
            setPosList((prev) => prev.map((item) => ({ ...item, isDefault: item.id === pos.id })));
        } catch (error) {
            alert(error.message || 'Impossible de définir ce terminal par défaut.');
        } finally {
            setActionLoadingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="caisses"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-6">
                    <DashboardHeader
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        currency={currency}
                        onCurrencyChange={setCurrency}
                        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    />

                    <div className="space-y-6 mt-6">
                        {/* En-tête */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent px-6 py-5">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                                            <PointOfSale className="text-white text-2xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-slate-800">Caisses</h1>
                                            <p className="text-slate-600 text-sm mt-0.5">
                                                {organizationId ? `${organizationName} · Caisses et terminaux` : 'Gérez vos caisses'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={loadPos}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 text-sm font-medium"
                                            disabled={!organizationId}
                                        >
                                            <Refresh fontSize="small" />
                                            Rafraîchir
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCreateModalOpen(true)}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all shadow-md hover:shadow-lg"
                                            disabled={!organizationId}
                                        >
                                            <Add className="text-lg" />
                                            Créer une caisse
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!organizationId && (
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-8 sm:p-12 text-center">
                                <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                                    <PointOfSale className="text-slate-400 text-3xl" />
                                </div>
                                <p className="text-slate-700 font-medium mb-1">Aucune organisation sélectionnée</p>
                                <p className="text-slate-500 text-sm max-w-sm mx-auto">Sélectionnez une organisation pour gérer vos caisses et terminaux.</p>
                            </div>
                        )}

                        {organizationId && (
                            <>
                            {/* Section Sites (points de vente) */}
                            <div className="hidden bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-slate-500/10 flex items-center justify-center">
                                            <LocationOn className="text-slate-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-800">Sites (points de vente)</h2>
                                            <p className="text-xs text-slate-500">{salePoints.length} site(s)</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={openNewSiteModal}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 text-sm shadow-sm"
                                    >
                                        <Add fontSize="small" />
                                        Ajouter un site
                                    </button>
                                </div>
                                <div className="p-5">
                                {loadingSalePoints ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-2" />
                                        <p className="text-slate-500 text-sm">Chargement des sites...</p>
                                    </div>
                                ) : salePoints.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 px-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/30">
                                        <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                                            <LocationOn className="text-slate-400 text-3xl" />
                                        </div>
                                        <p className="text-slate-700 font-medium mb-1">Aucun site</p>
                                        <p className="text-slate-500 text-sm text-center max-w-sm mb-4">Ajoutez un point de vente pour y rattacher des terminaux.</p>
                                        <button
                                            type="button"
                                            onClick={openNewSiteModal}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600"
                                        >
                                            <Add fontSize="small" />
                                            Ajouter un site
                                        </button>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto rounded-xl border border-slate-200/80">
                                        <table className="w-full min-w-[400px]">
                                            <thead>
                                                <tr className="border-b border-slate-200 bg-slate-50/80">
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Site</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Adresse</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Terminaux</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Statut</th>
                                                    <th className="text-right py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {salePoints.map((site) => {
                                                    const terminalCount = posList.filter((p) => (p.sale_point_id || p.site_id || p.salePoint?.id) === site.id).length;
                                                    return (
                                                        <tr key={site.id} className="hover:bg-slate-50/50 transition-colors">
                                                            <td className="py-3.5 px-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-9 h-9 rounded-lg bg-slate-500/10 flex items-center justify-center shrink-0">
                                                                        <LocationOn className="text-slate-600 text-lg" />
                                                                    </div>
                                                                    <span className="font-semibold text-slate-900">{site.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3.5 px-4 text-sm text-slate-500 max-w-[200px] truncate" title={site.address}>{site.address || '—'}</td>
                                                            <td className="py-3.5 px-4">
                                                                <span className="text-sm font-medium text-slate-700">{terminalCount} {terminalCount === 1 ? 'terminal' : 'terminaux'}</span>
                                                            </td>
                                                            <td className="py-3.5 px-4">
                                                                <span className={`inline-flex px-2.5 py-0.5 rounded-lg text-xs font-medium ${site.enabled !== false ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                                                                    {site.enabled !== false ? 'Actif' : 'Inactif'}
                                                                </span>
                                                            </td>
                                                            <td className="py-3.5 px-4 text-right">
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    <button type="button" onClick={() => openEditSiteModal(site)} disabled={loadingSiteAction} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50" title="Modifier">
                                                                        <Edit fontSize="small" />
                                                                    </button>
                                                                    <button type="button" onClick={() => handleDeleteSite(site)} disabled={loadingSiteAction} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50" title="Supprimer">
                                                                        <Delete fontSize="small" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                </div>
                            </div>

                            {/* Synthèse des encaissements par caissier */}
                            {!loading && !fetchError && (
                                <CashierEncashmentSummary
                                    posList={posList}
                                    encashmentsByCashier={encashmentsByCashier}
                                    cashierProfiles={cashierProfiles}
                                    currency={currency}
                                    onCurrencyChange={setCurrency}
                                    loading={loadingEncashments}
                                />
                            )}

                            {/* Section Terminaux (POS) — Table ou Cartes */}
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                            <PointOfSale className="text-amber-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-800">Terminaux (POS)</h2>
                                            <p className="text-xs text-slate-500">
                                                {posList.length} terminal(aux) configuré(s)
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs text-slate-500 mr-1">Vue :</span>
                                        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setTerminalViewMode('table')}
                                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${terminalViewMode === 'table' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                                                title="Vue tableau"
                                            >
                                                <ViewList fontSize="small" />
                                                Tableau
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setTerminalViewMode('cards')}
                                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${terminalViewMode === 'cards' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                                                title="Vue cartes"
                                            >
                                                <ViewModule fontSize="small" />
                                                Cartes
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5">
                                {loading && (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-2" />
                                        <p className="text-slate-500 text-sm">Chargement des terminaux...</p>
                                    </div>
                                )}

                                {fetchError && !loading && (
                                    <div className="flex items-center justify-center gap-2 py-12 px-4 text-red-600 bg-red-50/50">
                                        <ErrorOutline />
                                        <span className="text-sm font-medium">{fetchError}</span>
                                    </div>
                                )}

                                {!loading && !fetchError && posList.length === 0 && (
                                    <div className="flex flex-col items-center justify-center py-12 px-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/30">
                                        <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                                            <PointOfSale className="text-slate-400 text-3xl" />
                                        </div>
                                        <p className="text-slate-700 font-medium mb-1">Aucun terminal</p>
                                        <p className="text-slate-500 text-sm text-center max-w-sm mb-4">Créez un terminal en choisissant un caissier.</p>
                                        <button
                                            type="button"
                                            onClick={() => setCreateModalOpen(true)}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600"
                                        >
                                            <Add fontSize="small" />
                                            Nouveau terminal
                                        </button>
                                    </div>
                                )}

                                {!loading && !fetchError && posList.length > 0 && terminalViewMode === 'table' && (
                                    <div className="overflow-x-auto rounded-xl border border-slate-200/80">
                                        <table className="w-full min-w-[800px]">
                                            <thead>
                                                <tr className="border-b border-slate-200 bg-slate-50/80">
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Terminal</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Tag</th>
                                                    <th className="hidden text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Site</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Caissier</th>
                                                    <th className="text-left py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Créé le</th>
                                                    <th className="text-right py-3.5 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {posList.map((pos) => {
                                                    return (
                                                    <tr key={pos.id} className="hover:bg-slate-50/50 transition-colors">
                                                        <td className="py-3.5 px-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                                                                    <PointOfSale className="text-amber-600 text-lg" />
                                                                </div>
                                                                <div>
                                                                    <div className="font-semibold text-slate-900">{pos.name}</div>
                                                                    {pos.isDefault && (
                                                                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-600">
                                                                            <Star sx={{ fontSize: 14 }} /> Par défaut
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3.5 px-4">
                                                            <span className="font-mono text-sm text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{pos.tag || '—'}</span>
                                                        </td>
                                                        <td className="hidden py-3.5 px-4 text-sm text-slate-600">
                                                            {pos.salePoint?.name || (pos.sale_point_id && salePoints.find((s) => s.id === pos.sale_point_id)?.name) || '—'}
                                                        </td>
                                                        <td className="py-3.5 px-4">
                                                            {pos.cashier ? (
                                                                <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                                                                    <CheckCircle sx={{ fontSize: 16 }} />
                                                                    {pos.cashier}
                                                                </span>
                                                            ) : (
                                                                <span className="text-sm text-slate-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3.5 px-4 text-sm text-slate-500">
                                                            {pos.createdAt ? new Date(pos.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                        </td>
                                                        <td className="py-3.5 px-4 text-right">
                                                            <div className="flex items-center justify-end gap-1.5">
                                                                <button type="button" onClick={() => handleSetActivePos(pos)} disabled={pos.isDefault || actionLoadingId === pos.id} className={`p-2 rounded-lg border disabled:cursor-default ${pos.isDefault ? 'border-emerald-200 bg-emerald-50 text-emerald-600' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`} title={pos.isDefault ? 'Terminal par défaut' : 'Définir comme terminal par défaut'}>
                                                                    <Star fontSize="small" />
                                                                </button>
                                                                <button type="button" onClick={() => handleRenamePos(pos)} disabled={actionLoadingId === pos.id} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50" title="Renommer">
                                                                    <Edit fontSize="small" />
                                                                </button>
                                                                <button type="button" onClick={() => handleAssignCashier(pos)} disabled={actionLoadingId === pos.id} className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 disabled:opacity-50" title={pos.cashier ? 'Modifier caissier' : 'Assigner caissier'}>
                                                                    <PersonAddAlt1 fontSize="small" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {!loading && !fetchError && posList.length > 0 && terminalViewMode === 'cards' && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {posList.map((pos) => {
                                            return (
                                            <div key={pos.id} className="bg-slate-50/50 rounded-2xl border border-slate-200/80 p-5 hover:shadow-md hover:border-slate-300/80 transition-all duration-200">
                                                <div className="flex items-start justify-between gap-3 mb-4">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 flex items-center justify-center shrink-0">
                                                        <PointOfSale className="text-amber-600 text-2xl" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h3 className="font-semibold text-slate-900 truncate">{pos.name}</h3>
                                                        <span className="font-mono text-xs text-slate-500">{pos.tag || '—'}</span>
                                                    </div>
                                                    {pos.isDefault && (
                                                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                                                            <Star sx={{ fontSize: 12 }} /> Par défaut
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div className="hidden flex items-center gap-2 text-slate-600">
                                                        <LocationOn sx={{ fontSize: 18 }} className="text-slate-400" />
                                                        <span className="truncate">{pos.salePoint?.name || (pos.sale_point_id && salePoints.find((s) => s.id === pos.sale_point_id)?.name) || '—'}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {pos.cashier ? (
                                                            <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                                                                <CheckCircle sx={{ fontSize: 16 }} />
                                                                <span className="truncate">{pos.cashier}</span>
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400">Aucun caissier assigné</span>
                                                        )}
                                                    </div>
                                                    {pos.createdAt && (
                                                        <p className="text-xs text-slate-500">
                                                            Créé le {new Date(pos.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200/80">
                                                    <button type="button" onClick={() => handleSetActivePos(pos)} disabled={pos.isDefault || actionLoadingId === pos.id} className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-white text-xs font-medium disabled:opacity-60 disabled:cursor-default">
                                                        <Star fontSize="small" /> {pos.isDefault ? 'Par défaut' : 'Définir par défaut'}
                                                    </button>
                                                    <button type="button" onClick={() => handleRenamePos(pos)} disabled={actionLoadingId === pos.id} className="inline-flex items-center justify-center p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-50" title="Renommer">
                                                        <Edit fontSize="small" />
                                                    </button>
                                                    <button type="button" onClick={() => handleAssignCashier(pos)} disabled={actionLoadingId === pos.id} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50">
                                                        <PersonAddAlt1 fontSize="small" /> {pos.cashier ? 'Modifier' : 'Assigner'}
                                                    </button>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                                </div>
                            </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <CreatePosModal
                open={createModalOpen}
                onClose={() => {
                    setCreateModalOpen(false);
                    resetModal();
                }}
                onSubmit={handleCreatePos}
                form={formState}
                onChange={(field, value) => setFormState(prev => ({ ...prev, [field]: value }))}
                cashiers={cashiers}
                loadingCashiers={loadingCashiers}
                submitting={creatingPos}
                errorMessage={modalError}
                onCreateCashier={() => setCreateCashierModalOpen(true)}
            />

            {/* Modal Site (point de vente) — créer / modifier */}
            {false && siteModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-800">
                                {editingSiteId ? 'Modifier le site' : 'Ajouter un site'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => { setSiteModalOpen(false); setModalError(''); }}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                            >
                                Fermer
                            </button>
                        </div>
                        <form onSubmit={handleSaveSite} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Nom du site *</label>
                                <input
                                    type="text"
                                    value={siteForm.name}
                                    onChange={(e) => setSiteForm((prev) => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="ex. Agence Centre"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                                <input
                                    type="text"
                                    value={siteForm.address}
                                    onChange={(e) => setSiteForm((prev) => ({ ...prev, address: e.target.value }))}
                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="ex. 123 rue Example, Ville"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="site-enabled"
                                    checked={siteForm.enabled}
                                    onChange={(e) => setSiteForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                                    className="rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                />
                                <label htmlFor="site-enabled" className="text-sm text-slate-700">Site actif</label>
                            </div>
                            {modalError && (
                                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-2 rounded-xl">
                                    {modalError}
                                </div>
                            )}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setSiteModalOpen(false); setModalError(''); }}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loadingSiteAction || !siteForm.name.trim()}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-50"
                                >
                                    {loadingSiteAction ? 'Enregistrement...' : (editingSiteId ? 'Enregistrer' : 'Créer le site')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal pour assigner un caissier (créer une caisse) */}
            {assignCashierModalOpen && selectedPosForCashier && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-800">Assigner un caissier</h3>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    Terminal <span className="font-mono text-slate-700">{selectedPosForCashier.tag || selectedPosForCashier.name}</span>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setAssignCashierModalOpen(false);
                                    setSelectedPosForCashier(null);
                                    setAssignCashierForm({ phoneNumber: '', selectedCashierId: '' });
                                    setModalError('');
                                }}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                            >
                                Fermer
                            </button>
                        </div>

                        <form onSubmit={handleSubmitAssignCashier} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Sélectionner un caissier existant
                                </label>
                                {loadingCashiers ? (
                                    <div className="w-full px-4 py-3 border border-slate-300 rounded-xl text-center text-slate-500">
                                        Chargement des caissiers...
                                    </div>
                                ) : cashiers.length === 0 ? (
                                    <div className="space-y-3">
                                        <div className="w-full px-4 py-3 border border-slate-300 rounded-xl text-center text-slate-500 bg-slate-50">
                                            Aucun caissier trouvé
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setCreateCashierModalOpen(true)}
                                            className="w-full px-4 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
                                        >
                                            Créer un caissier
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <select
                                            value={assignCashierForm.selectedCashierId}
                                            onChange={(e) => setAssignCashierForm(prev => ({ 
                                                ...prev, 
                                                selectedCashierId: e.target.value,
                                                phoneNumber: e.target.value ? '' : prev.phoneNumber
                                            }))}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        >
                                            <option value="">Choisir un caissier...</option>
                                            {cashiers.map((cashier) => {
                                                const user = cashier.user || {};
                                                const phone = user.phone?.fullNumber || user.phoneNumber || '';
                                                const name = user.profile?.completeName || user.name || phone || 'Caissier';
                                                return (
                                                    <option key={cashier.id} value={user.id}>
                                                        {name} {phone ? `(${phone})` : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setCreateCashierModalOpen(true)}
                                            className="w-full px-3 py-2 text-sm rounded-xl border border-amber-300 text-amber-600 hover:bg-amber-50"
                                        >
                                            + Créer un nouveau caissier
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-slate-500">OU</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Entrer un numéro de téléphone Netikash
                                </label>
                                <input
                                    type="tel"
                                    value={assignCashierForm.phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\s+/g, '');
                                        setAssignCashierForm(prev => ({ 
                                            ...prev, 
                                            phoneNumber: value,
                                            selectedCashierId: prev.selectedCashierId && value ? '' : prev.selectedCashierId
                                        }));
                                    }}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="243890000000"
                                    disabled={!!assignCashierForm.selectedCashierId}
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    Le numéro doit être associé à un compte Netikash avec le rôle caissier.
                                </p>
                            </div>

                            {modalError && (
                                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-2 rounded-xl">
                                    {modalError}
                                </div>
                            )}

                            <div className="flex space-x-3 pt-2">
                                <button
                                    type="button"
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                    onClick={() => {
                                        setAssignCashierModalOpen(false);
                                        setSelectedPosForCashier(null);
                                        setAssignCashierForm({ phoneNumber: '', selectedCashierId: '' });
                                        setModalError('');
                                    }}
                                    disabled={actionLoadingId === selectedPosForCashier.id}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                                    disabled={actionLoadingId === selectedPosForCashier.id || (!assignCashierForm.selectedCashierId && !assignCashierForm.phoneNumber.trim())}
                                >
                                    {actionLoadingId === selectedPosForCashier.id ? 'Assignation...' : 'Créer la Caisse'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal pour créer un caissier */}
            {createCashierModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-800">Créer un caissier</h3>
                                <p className="text-sm text-slate-500 mt-0.5">Ajouter le rôle caissier à un utilisateur Netikash</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setCreateCashierModalOpen(false);
                                    setCreateCashierForm({ phoneNumber: '' });
                                    setModalError('');
                                }}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                            >
                                Fermer
                            </button>
                        </div>

                        <form onSubmit={handleCreateCashier} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Numéro de téléphone Netikash *
                                </label>
                                <input
                                    type="tel"
                                    value={createCashierForm.phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\s+/g, '');
                                        setCreateCashierForm(prev => ({ ...prev, phoneNumber: value }));
                                    }}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="243890000000"
                                    required
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    Le numéro doit être associé à un compte Netikash existant. Le rôle caissier sera ajouté à cet utilisateur.
                                </p>
                            </div>

                            {modalError && (
                                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-2 rounded-xl">
                                    {modalError}
                                </div>
                            )}

                            <div className="flex space-x-3 pt-2">
                                <button
                                    type="button"
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                    onClick={() => {
                                        setCreateCashierModalOpen(false);
                                        setCreateCashierForm({ phoneNumber: '' });
                                        setModalError('');
                                    }}
                                    disabled={creatingCashier}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                                    disabled={creatingCashier || !createCashierForm.phoneNumber.trim()}
                                >
                                    {creatingCashier ? 'Création...' : 'Créer le Caissier'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaisseManagement;
